import { useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { Badge } from '@/components/Badge'
import type {
  CockpitPageDefinition,
  CockpitSignal,
  CockpitSubview,
} from '@/features/cockpit/cockpitData'

const periodOptions = ['7 Tage', '30 Tage', '90 Tage', 'Dieses Jahr'] as const
const productTypeOptions = ['Alle Produkttypen', 'Karten', 'Textilien', 'Deko'] as const

const signalToneStyles: Record<CockpitSignal['tone'], string> = {
  positive: 'bg-emerald-500 dark:bg-emerald-400',
  watch: 'bg-amber-500 dark:bg-amber-400',
  neutral: 'bg-blue-500 dark:bg-blue-400',
}

function KpiCard({
  label,
  value,
  context,
}: {
  readonly label: string
  readonly value: string
  readonly context: string
}): ReactNode {
  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="mt-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
        {value}
      </p>
      <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{context}</p>
    </article>
  )
}

function SignalRow({ signal }: { readonly signal: CockpitSignal }): ReactNode {
  return (
    <article className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100">{signal.title}</h3>
          <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
            {signal.detail}
          </p>
        </div>
        <span className="text-sm font-bold text-slate-900 dark:text-slate-100">{signal.value}</span>
      </div>
      <div
        className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
        aria-hidden="true"
      >
        <div
          className={`h-full rounded-full ${signalToneStyles[signal.tone]}`}
          style={{ width: String(signal.strength) + '%' }}
        />
      </div>
    </article>
  )
}

function SubviewButton({
  subview,
  isActive,
  onSelect,
}: {
  readonly subview: CockpitSubview
  readonly isActive: boolean
  readonly onSelect: () => void
}): ReactNode {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      onClick={onSelect}
      className={
        isActive
          ? 'rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition'
          : 'rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600'
      }
    >
      {subview.label}
    </button>
  )
}

export function CockpitDetailPage({
  definition,
}: {
  readonly definition: CockpitPageDefinition
}): ReactNode {
  const [period, setPeriod] = useState<string>('30 Tage')
  const [productType, setProductType] = useState<string>('Alle Produkttypen')
  const [activeSubviewId, setActiveSubviewId] = useState<string>('overview')
  const activeSubview =
    definition.subviews.find((subview) => subview.id === activeSubviewId) ?? definition.subviews[0]

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/"
          className="text-sm font-medium text-blue-700 underline-offset-2 hover:underline dark:text-blue-300"
        >
          Zur Übersicht
        </Link>
        <Badge>Demo-Daten, keine Live-Auswertung</Badge>
      </div>

      <header className="mb-6">
        <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
          {definition.eyebrow}
        </p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          {definition.title}
        </h1>
        <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600 dark:text-slate-300">
          {definition.description}
        </p>
      </header>

      <section
        aria-label="Hinweis zu fehlenden Live-Daten"
        className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
      >
        <h2 className="font-semibold">Struktur steht, Live-Schnittstellen fehlen noch</h2>
        <p className="mt-1 text-sm leading-6">
          Die folgenden Werte und Signale sind Dummy-Inhalte für die erste Version. Die Oberfläche
          zeigt bereits, wo später echte Daten, Aktualisierungen und Handlungsempfehlungen
          erscheinen.
        </p>
      </section>

      <section
        aria-label="Analysefilter"
        className="mb-6 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
      >
        <div className="grid gap-4 md:grid-cols-3">
          <label>
            <span className="mb-2 block text-xs font-medium text-slate-600 dark:text-slate-300">
              Zeitraum
            </span>
            <select
              value={period}
              onChange={(event) => {
                setPeriod(event.target.value)
              }}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none ring-blue-500 focus:ring-2 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            >
              {periodOptions.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="mb-2 block text-xs font-medium text-slate-600 dark:text-slate-300">
              Produkttyp
            </span>
            <select
              value={productType}
              onChange={(event) => {
                setProductType(event.target.value)
              }}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none ring-blue-500 focus:ring-2 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            >
              {productTypeOptions.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="mb-2 block text-xs font-medium text-slate-600 dark:text-slate-300">
              Datenquelle
            </span>
            <select
              disabled
              defaultValue="demo"
              className="w-full cursor-not-allowed rounded-lg border border-slate-300 bg-slate-100 px-3 py-2 text-sm text-slate-500 outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400"
            >
              <option value="demo">{definition.demoSource}</option>
            </select>
          </label>
        </div>
        <p className="mt-3 text-xs text-amber-700 dark:text-amber-300">
          Die Demo-Filter sind für den Wireframe bedienbar. Die Dummy-Werte werden noch nicht aus
          einer Schnittstelle neu berechnet.
        </p>
      </section>

      <section aria-label="Kennzahlen" className="mb-8">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Kennzahlen</h2>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Auswahl: {period}, {productType}
          </span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {definition.kpis.map((kpi) => (
            <KpiCard key={kpi.label} label={kpi.label} value={kpi.value} context={kpi.context} />
          ))}
        </div>
      </section>

      <section aria-label="Unteransichten" className="mb-8">
        <div className="mb-3">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Unteransichten</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            Die Detailstruktur ist für spätere echte Auswertungen vorbereitet.
          </p>
        </div>
        <div role="tablist" aria-label="Unteransicht" className="flex flex-wrap gap-2">
          {definition.subviews.map((subview) => (
            <SubviewButton
              key={subview.id}
              subview={subview}
              isActive={activeSubviewId === subview.id}
              onSelect={() => {
                setActiveSubviewId(subview.id)
              }}
            />
          ))}
        </div>
        {activeSubview === undefined ? null : (
          <div className="mt-3 rounded-lg bg-slate-50 p-4 text-sm text-slate-600 dark:bg-slate-900 dark:text-slate-300">
            <span className="font-semibold text-slate-900 dark:text-slate-100">
              {activeSubview.label}:
            </span>{' '}
            {activeSubview.description}
          </div>
        )}
      </section>

      <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <section
          aria-label="Signale"
          className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800"
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Signale</h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                Dummy-Rangfolge als Platzhalter für die spätere Auswertung.
              </p>
            </div>
            <Badge>Demo</Badge>
          </div>
          <div className="space-y-3">
            {definition.signals.map((signal) => (
              <SignalRow key={signal.title} signal={signal} />
            ))}
          </div>
        </section>

        <section
          aria-label="Datenquelle und Aktualität"
          className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800"
        >
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
            Datenquelle und Aktualität
          </h2>
          <dl className="mt-4 space-y-4 text-sm">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Datenquelle
              </dt>
              <dd className="mt-1 text-slate-800 dark:text-slate-200">{definition.demoSource}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Letzte Aktualisierung
              </dt>
              <dd className="mt-1 text-slate-800 dark:text-slate-200">{definition.updatedAt}</dd>
            </div>
          </dl>
          <p className="mt-4 rounded-lg bg-amber-50 p-3 text-xs leading-5 text-amber-900 dark:bg-amber-950 dark:text-amber-100">
            Aktualisierungsrhythmus und Datenvollständigkeit sind für die echte Anbindung noch zu
            definieren.
          </p>
        </section>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <section
          aria-label="Berechnungslogik"
          className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800"
        >
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Berechnung</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {definition.calculationNote}
          </p>
        </section>
        <section
          aria-label="Datenabhängigkeiten und Grenzen"
          className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800"
        >
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
            Datenabhängigkeiten und Grenzen
          </h2>
          <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {definition.dependencies.map((dependency) => (
              <li key={dependency} className="flex items-start gap-2">
                <span className="mt-1 text-amber-600 dark:text-amber-300" aria-hidden="true">
                  ●
                </span>
                <span>{dependency}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  )
}
